import React from 'react';
import { Subtract } from 'utility-types';
import { IInjectedModalProps, ModalContext } from './types';

/**
 * Injector HOC. Inject context's props into ModalBody, ModalHeader, ModalCloseBtn AND removes injected props so they can not be set outside.
 * This will let us to access context via props,
 * instead of calling ModalContext.Consumer in lifecycle methods
 *
 * The generic <P extends IInjectedModalProps>(Component: React.ComponentType<P>)
 * ensures that the component passed into the HOC includes the props
 * that are going to be injected by it.
 *
 * The generic React.Component<Subtract<P, IInjectedModalProps>>
 * the component returned by the HOC uses Subtract, which will subtract the injected props from the passed in component’s props,
 * meaning that if they are set on the resulting wrapped component you will receive a compilation error:
 *
 * @see {@link https://medium.com/@jrwebdev/react-higher-order-component-patterns-in-typescript-42278f7590fb}
 */
export const withModalContext = <P extends IInjectedModalProps>(Component: React.ComponentType<P>) =>
  class ComponentWithModalContext extends React.Component<Subtract<P, IInjectedModalProps>> {
    public render() {
      const { ...props } = this.props as P;
      return <ModalContext.Consumer>{(context) => <Component {...props} {...context} />}</ModalContext.Consumer>;
    }
  };
